<svelte:options runes={true} />

<script lang="ts">
  import Text from "~/components/ui/Text/Text.svelte";
  import Input from "~/components/ui/Input/Input.svelte";
  import Button from "~/components/ui/Button/Button.svelte";

  type Props = {
    query: string;
    settledQuery?: string;
    autocorrectQuery?: string;
    didYouMean?: string[];
    onSuggestionClick?: (suggestion: string) => void;
    onRevert?: () => void;
  };

  let {
    query = $bindable(),
    settledQuery,
    autocorrectQuery,
    didYouMean,
    onSuggestionClick,
    onRevert,
  }: Props = $props();

  const displayQuery = $derived(settledQuery ?? query);
  const headingQuery = $derived(autocorrectQuery ?? displayQuery);
</script>

<header class="da-search-header">
  <div class="da-search-header__inner">
    <Text as="h1" family="display" size="3xl" weight="regular" class="da-search-header__heading">
      Search results for: &#x201c;{headingQuery}&#x201d;
    </Text>

    {#if autocorrectQuery}
      <p class="da-search-header__autocorrect">
        <Button
          size="sm"
          variant="link"
          class="da-search-header__autocorrect-revert"
          onclick={onRevert}
        >
          Search instead for &#x201c;{displayQuery}&#x201d;
        </Button>
      </p>
    {/if}

    {#if didYouMean && didYouMean.length > 0}
      <div class="da-search-header__suggestions" role="group" aria-label="Did you mean">
        <span class="da-search-header__suggestions-label">Did you mean</span>
        {#each didYouMean as suggestion (suggestion)}
          <Button
            size="sm"
            variant="primary"
            class="da-search-header__suggestion-pill"
            onclick={() => onSuggestionClick?.(suggestion)}
          >
            {suggestion}
          </Button>
        {/each}
      </div>
    {/if}

    <div class="da-search-header__input-wrapper">
      <Input variant="search" bind:value={query} placeholder="Search" aria-label="Search query" />
    </div>
  </div>
</header>

<style>
  @layer aurora {
    .da-search-header {
      container-type: inline-size;
      overflow-x: clip;
      background: var(--da-color-brand-sand-500);
    }

    .da-search-header__inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--da-space-2);
      padding-block: var(--da-space-3);
      padding-inline: var(--da-space-4);
      text-align: center;
    }

    :global(.da-search-header__heading.da-text) {
      font-size: var(--da-font-size-2xl) !important;
      max-width: 800px;
    }

    .da-search-header__autocorrect {
      margin: 0;
      font-size: var(--da-font-size-sm);
      color: var(--da-color-charcoal-400);
    }

    .da-search-header__suggestions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: var(--da-space-2);
      padding-block: var(--da-space-2);
    }

    .da-search-header__suggestions-label {
      width: 100%;
      font-size: var(--da-font-size-sm);
      color: var(--da-color-charcoal-400);
    }

    .da-search-header__input-wrapper {
      width: 100%;
      max-width: 560px;
    }

    @container (width >= 500px) {
      .da-search-header__inner {
        gap: var(--da-space-4);
        padding-block: var(--da-space-6);
      }

      :global(.da-search-header__autocorrect-revert) {
        font-size: var(--da-font-size-md);
      }

      :global(.da-search-header__heading.da-text) {
        font-size: var(--da-font-size-3xl) !important;
      }

      .da-search-header__suggestions-label {
        width: auto;
      }
    }
  }
</style>
